// HRIS confirmation (PLAN D6; CR-059, CR-060). The Coordinator (and so the Recruiter) confirms the HRIS entry (matrix
// "Validate/Complete"; his HR Operations folds into the Coordinator, DC-228) and the Admin does too, as its own act: no
// override path, no reason header. HM, HRBP and TA Manager read the facts (matrix "View"). Final terms arrive already trimmed by
// field policy: a key this seat may not read is absent and is never drawn. Filled derives from this card, never from acceptance (DC-206).

import { useState } from "react";
import {
  ACCESS_LABELS, ACTION_LABELS, Button, Card, COMMON_LABELS, ERROR_LABELS, Field, G1_LABELS, IdPill, Input, KeyValue, Pill,
  REQUISITION_STATUS, ROLE_LABELS, Stack, TextArea, formatDate, type KeyValueItem,
} from "@/mc";
import { CLOSURE_LABELS, DATE_LABELS, HRIS_LABELS, TERMS_LABELS, unconfirmed } from "./labels";
import { slug } from "./slug";
import type { CloseoutApplication, RunWrite } from "./types";

const day = (value: string | null | undefined) => (value ? formatDate(value) : "—");
/** A money or count term as the server sent it (a number or its decimal string), or — when absent. */
const term = (value: unknown) => (value == null || value === "" ? "—" : String(value));

function termsItems(app: CloseoutApplication): KeyValueItem[] {
  const terms = app.final_terms;
  if (!terms) return [];
  const items: KeyValueItem[] = [];
  if ("start_date" in terms) items.push([DATE_LABELS.startDate, day(terms.start_date)]);
  if ("base" in terms) items.push([TERMS_LABELS.base, terms.base == null ? "—" : `${term(terms.base)}${terms.currency ? ` ${terms.currency}` : ""}`]);
  if ("bonus_target" in terms) items.push([TERMS_LABELS.bonus, term(terms.bonus_target)]);
  if ("sign_on" in terms) items.push([TERMS_LABELS.signOn, term(terms.sign_on)]);
  if ("relocation" in terms) items.push([TERMS_LABELS.relocation, term(terms.relocation)]);
  if ("lti_present" in terms) items.push([TERMS_LABELS.lti, terms.lti_present == null ? "—" : terms.lti_present ? ACCESS_LABELS.Yes : ACCESS_LABELS.No]);
  return items;
}

export interface HrisCardProps {
  app: CloseoutApplication;
  runWrite: RunWrite;
  /** The archived record is read-only: no confirm, no revision (S12's archivedCan). */
  readOnly?: boolean | undefined;
}

export function HrisCard({ app, runWrite, readOnly }: HrisCardProps) {
  const id = app.application_id;
  const mayConfirm = !readOnly && !app.hris && !!app.ready_for_hris_at && app.can.confirm;
  const mayCorrect = !readOnly && !!app.hris && app.can.correct;
  const [employee, setEmployee] = useState(app.hris?.employee_id ?? "");
  const [hireDate, setHireDate] = useState(app.evidence?.hire_date ?? "");
  const [startDate, setStartDate] = useState(app.evidence?.start_date ?? app.final_terms?.start_date ?? "");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [correcting, setCorrecting] = useState(false);
  const [correctReason, setCorrectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const ready = employee.trim() && hireDate && startDate;

  const run = async (write: Parameters<RunWrite>[0]) => {
    setBusy(true);
    try {
      await runWrite(write);
      setCorrecting(false);
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => run({
    tool: "hris.confirm",
    input: {
      application_id: id, employee_id: employee.trim(), hire_date: hireDate, start_date: startDate,
      evidence_ref: evidenceRef.trim() || null, hris_evidence_id: app.evidence?.hris_evidence_id ?? null,
    },
  });

  const correct = () => app.hris && run({
    tool: "hris.correct",
    input: {
      application_id: id, confirmation_id: app.hris.confirmation_id,
      employee_id: employee.trim() && employee.trim() !== app.hris.employee_id ? employee.trim() : null,
      hire_date: hireDate && hireDate !== app.hris.hire_date ? hireDate : null,
      start_date: startDate && startDate !== app.hris.start_date ? startDate : null,
      evidence_ref: evidenceRef.trim() || null, reason: correctReason.trim(),
    },
  });

  const status = app.hris ? REQUISITION_STATUS.hrisHired : app.ready_for_hris_at ? REQUISITION_STATUS.readyForHris : null;
  const terms = termsItems(app);

  const facts: KeyValueItem[] = app.hris
    ? [
        [unconfirmed("employeeId"), app.hris.employee_id ?? "—"],
        [DATE_LABELS.hrisHire, day(app.hris.hire_date)],
        [DATE_LABELS.startDate, day(app.hris.start_date)],
        [HRIS_LABELS.source, app.hris.source === "adapter" && app.hris.correlation_id
          ? <IdPill value={app.hris.correlation_id} label={ERROR_LABELS.correlationId} />
          : ROLE_LABELS.coordinator],
        [HRIS_LABELS.evidence, app.hris.evidence_ref ?? "—"],
      ]
    : [];

  const form = (
    <Stack>
      <Field label={unconfirmed("employeeId")} required>
        <Input value={employee} onChange={(event) => setEmployee(event.target.value)} mono hs={`hris.employee.${slug(id)}`} />
      </Field>
      <Field label={DATE_LABELS.hrisHire} required>
        <Input type="date" value={hireDate} onChange={(event) => setHireDate(event.target.value)} hs={`hris.hire-date.${slug(id)}`} />
      </Field>
      <Field label={DATE_LABELS.startDate} required>
        <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} hs={`hris.start-date.${slug(id)}`} />
      </Field>
      <Field label={HRIS_LABELS.evidence} optional>
        <Input value={evidenceRef} onChange={(event) => setEvidenceRef(event.target.value)} hs={`hris.evidence.${slug(id)}`} />
      </Field>
    </Stack>
  );

  return (
    <Card title={CLOSURE_LABELS.hrisConfirmation} sub={app.display_name ?? app.candidate_token}
      pills={status ? <Pill>{status}</Pill> : undefined}>
      <Stack>
        {facts.length ? <KeyValue items={facts} /> : null}
        {terms.length ? (
          <Card title={CLOSURE_LABELS.finalTerms} tight>
            <KeyValue items={terms} />
          </Card>
        ) : null}
        {mayConfirm ? (
          <>
            {form}
            <Button tone="primary" busy={busy} onClick={confirm} hs={`hris.confirm.${slug(id)}`}
              blocked={ready ? undefined : { reason: COMMON_LABELS.required }}>
              {ACTION_LABELS.confirmHris}
            </Button>
          </>
        ) : null}
        {mayCorrect && !correcting ? (
          <Button sm onClick={() => setCorrecting(true)} hs={`hris.correct.${slug(id)}`}>{ACTION_LABELS.revise}</Button>
        ) : null}
        {mayCorrect && correcting ? (
          <>
            {form}
            <Field label={G1_LABELS.reason} required>
              <TextArea rows={2} value={correctReason} onChange={(event) => setCorrectReason(event.target.value)} hs={`hris.correct-reason.${slug(id)}`} />
            </Field>
            <Button tone="primary" busy={busy} onClick={correct} hs={`hris.correct-save.${slug(id)}`}
              blocked={correctReason.trim().length >= 3 ? undefined : { reason: COMMON_LABELS.required }}>
              {ACTION_LABELS.save}
            </Button>
          </>
        ) : null}
      </Stack>
    </Card>
  );
}
